import { FC, useMemo, useState } from 'react';
import { Table } from 'antd';
import _ from 'lodash';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { Attention, RefreshIcon } from '@dynamika-shared/ui/icons';
import { CircleDivider } from '@dynamika-shared/ui/CircleDivider/CircleDivider';
import { Collapse } from '@dynamika-shared/ui/Collapse/Collapse';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { SearchSuffix } from '@dynamika-shared/ui/Input/SearchSuffix';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { SkeletonLine } from '@dynamika-shared/ui/Skeleton/Skeleton';
import { shortDate, shortDateTime } from '@dynamika-shared/utils/date';
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue';
import { ExecutionStatus } from '@dynamika-shared/types/executions';
import {
  QCTReading,
  QCTWorkstationDataDTO,
} from '@dynamika-shared/types/QCTReading';
import { BottomConfirmation } from 'src/components/BottomConfirmation/BottomConfirmation';
import {
  countActiveSelections,
  excludeVisitReadings,
  QCTScanTypeLabels,
  toAcquisitionDate,
  toWorkstationReadings,
} from 'src/utils/qctHelpers';
import { Label } from '@restore/clinicalTrialContext/clinicalTrialContext.types';
import { CollapseHeader } from '../components/CollapseHeader';
import { makeColumns } from './Table.columns';
import './Table.scss';

const VIRTUAL_ROWS_QTY = 10;
const ROW_HEIGHT = 77;
const MIN_SEARCH_LENGTH = 3;

/**
 * Bucket for results the workstation returned without a usable exam date.
 *
 * They must stay visible — silently dropping a result the specialist may need
 * to label is worse than showing it under an explicit "no date" heading — but
 * they cannot participate in date sorting, so they are pinned to the end.
 */
const UNDATED_GROUP = 'UNDATED';

type WorkstationResultsTableProps = {
  onSync: () => void;
  sendToReadings: (val: QCTReading[]) => void;
  lastSync: string;
  syncStatus: ExecutionStatus;
  labels: Label[];
  workstationData: QCTWorkstationDataDTO[];
  /** The visit's reading list — those results are hidden here. */
  sentForReading: QCTReading[];
  loading: boolean;
  readOnly: boolean;
};

export const WorkstationResultsTable: FC<WorkstationResultsTableProps> = ({
  workstationData,
  sentForReading,
  lastSync,
  syncStatus,
  sendToReadings,
  onSync,
  labels,
  loading,
  readOnly,
}) => {
  const [collapsedIndex, setCollapsedIndex] = useState(0);
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [searchValue, setSearchValue, debouncedValue] = useDebouncedValue('');

  // A label already picked in this session disappears from the remaining
  // dropdowns — one label describes exactly one result (D7-2775).
  const availableLabels = useMemo(() => {
    const takenLabelIds = new Set(Object.values(selected));

    return labels.filter(label => !takenLabelIds.has(label.id));
  }, [labels, selected]);

  const syncedReadings = useMemo(() => {
    if (loading) {
      return [];
    }

    return toWorkstationReadings(workstationData);
  }, [workstationData, loading]);

  /**
   * What is still left to pick.
   *
   * Plain terms
   * -----------
   * A result that has been saved for reading now lives in the Readings table
   * above, so it leaves this one. It comes back the moment it is removed from
   * the reading list.
   *
   * Technical
   * ---------
   * Filtered here, before search, grouping and the selection count, so a row
   * the visit already holds is gone from every one of them — including
   * `onSend`, which composes its payload from this list and could otherwise
   * re-send a scan whose label the visit has already taken.
   */
  const workstationReadings = useMemo(
    () => excludeVisitReadings(syncedReadings, sentForReading),
    [syncedReadings, sentForReading]
  );

  const allSelectedForReading =
    !!syncedReadings.length && !workstationReadings.length;

  /**
   * Picking a scan for reading.
   *
   * Plain terms
   * -----------
   * One workstation record can describe several scans — typically a lumbar
   * spine and a hip of the same exam. Each is its own result and carries its
   * own label, so labelling one leaves the others exactly as they were.
   *
   * Technical
   * ---------
   * Selections are keyed by `rowKey` (`<record id>:<scan position>`), which is
   * what separates two scans of one record; `referenceId` is shared by both and
   * cannot key this. `mergeLabeledScores` matches a stored reading by its scan
   * rather than by its record, so a payload holding both scans of a record is
   * one the visit can store (D7-2775).
   */
  const onSelect = (rowKey: string, labelId: string | null) => {
    setSelected(prev => {
      if (!labelId) {
        const next = { ...prev };
        delete next[rowKey];

        return next;
      }

      return { ...prev, [rowKey]: labelId };
    });
  };

  const isSearching = debouncedValue.length >= MIN_SEARCH_LENGTH;

  /**
   * What the search looks through.
   *
   * Plain terms
   * -----------
   * Searching runs over the workstation's own result — every value it reported,
   * not only the three the row puts on screen. Typing a file name, a density, an
   * exam number or the scanner's name all find the result they belong to.
   *
   * Technical
   * ---------
   * `reading.entry` is the raw `QCTWorkstationEntry`, walked recursively, so the
   * nested CSV column map is covered without naming a single column here. The
   * region label is searched alongside it because it is this app's word for
   * `bodyProperty` — "Lumbar Spine" is never what the entry spells.
   */
  const foundReadings = useMemo(() => {
    if (!isSearching) {
      return workstationReadings;
    }

    const needle = debouncedValue.toLowerCase();

    const matches = (value: unknown): boolean =>
      value !== null &&
      typeof value === 'object'
        ? Object.values(value).some(matches)
        : String(value).toLowerCase().includes(needle);

    return workstationReadings.filter(reading =>
      [
        reading.scanType && QCTScanTypeLabels[reading.scanType],
        reading.entry,
      ].some(matches)
    );
  }, [workstationReadings, debouncedValue, isSearching]);

  const workstationReadingsGrouped = useMemo(() => {
    if (loading) {
      return {};
    }

    // Grouped by the calendar date the workstation reported, kept as a string:
    // `toAcquisitionDate` never builds an instant, so a group cannot slide into
    // the previous day west of UTC, and a date nobody can read joins UNDATED.
    return _.groupBy(
      foundReadings,
      reading => toAcquisitionDate(reading.examDate) ?? UNDATED_GROUP
    );
  }, [foundReadings, loading]);

  // Newest acquisition date first — the specialist almost always works on the most
  // recent sync, and the oldest group being open by default hides it.
  const workstationReadingEntries = useMemo(
    () =>
      Object.entries(workstationReadingsGrouped).sort(([a], [b]) => {
        if (a === UNDATED_GROUP) {
          return 1;
        }

        if (b === UNDATED_GROUP) {
          return -1;
        }

        // `YYYY-MM-DD` sorts lexicographically the way it sorts in time.
        return b.localeCompare(a);
      }),
    [workstationReadingsGrouped]
  );

  const presentRowKeys = useMemo(
    () => new Set(workstationReadings.map(reading => reading.rowKey)),
    [workstationReadings]
  );

  const selectedLength = useMemo(
    () => countActiveSelections(selected, presentRowKeys),
    [selected, presentRowKeys]
  );

  // Nothing in the columns depends on the date group, so they are built once:
  // a fresh array per group would re-render every virtualised table.
  const columns = useMemo(
    () =>
      makeColumns({
        mode: 'workstation',
        allLabels: labels,
        availableLabels,
        onSelect,
        selected,
        readOnly,
      }),
    [labels, availableLabels, selected, readOnly]
  );

  const onSend = () => {
    if (!Object.keys(selected).length) {
      return;
    }

    const dataToSend = workstationReadings
      .filter(reading => !!selected[reading.rowKey])
      .map(reading => ({ ...reading, labelId: selected[reading.rowKey] }));

    sendToReadings(dataToSend);
    setSelected({});
  };

  let emptyStateTitle = (
    <span className='text-spanishGray'>
      No <b className='text-black'>Workstation Results</b> synchronized yet!
    </span>
  );

  if (allSelectedForReading) {
    emptyStateTitle = (
      <span className='text-spanishGray'>
        All <b className='text-black'>Workstation Results</b> are already
        selected for reading.
      </span>
    );
  }

  if (isSearching) {
    emptyStateTitle = (
      <span className='text-spanishGray'>
        No <b className='text-black'>Workstation Results</b> match this search.
      </span>
    );
  }

  const isSyncing = syncStatus === ExecutionStatus.IN_PROGRESS;

  let syncDateTime = 'No Data';

  if (isSyncing) {
    syncDateTime = 'In Progress';
  }

  if (lastSync) {
    syncDateTime = shortDateTime(lastSync);
  }

  return (
    <div>
      <div className='flex justify-between items-center w-full'>
        <div className='flex items-center w-full'>
          <h3 className='text-[2.4rem] leading-[3.2rem] font-semibold my-4'>
            Workstation Results
          </h3>
          {readOnly && (
            <span className='mx-[0.6rem]'>
              <Attention className='text-vividTangelo' />
            </span>
          )}
          <CircleDivider />
          <div className='flex text-1624 leading-6 font-medium mr-4'>
            <span>Last Sync:</span>
            <span className='text-dimGray ml-1'>{syncDateTime}</span>
          </div>

          {isSyncing && (
            <div className='flex items-center text-1420 mr-4'>
              <RefreshIcon className='h-[14px] w-[14px] mr-[.8rem] text-lg text-dodgerBlue animate-spin' />
              <div className='text-dodgerBlue'>Synchronizing</div>
            </div>
          )}

          {!isSyncing && !readOnly && (
            <Button
              type='primary'
              data-testid='QCTSyncWorkstation'
              onClick={onSync}
              className='!bg-blackOlive rounded-md items-center hover:border-waterLight border-black !px-1.5 !py-1 hover:!bg-waterLight hover:!text-black'
            >
              <RefreshIcon className='h-[14px] w-[14px] mr-[.8rem] text-lg' />
              <span className='font-normal text-1420'>Sync</span>
            </Button>
          )}
        </div>

        <div className='flex justify-between'>
          <Input
            value={searchValue}
            data-testid='QCTWorkstationSearch'
            onChange={e => setSearchValue(e.target.value)}
            className='!mt-0 h-[4.8rem] min-w-[32rem] !rounded-[3.2rem] px-2'
            placeholder='Search'
            suffix={
              <SearchSuffix
                disabled={!searchValue}
                action={() => setSearchValue('')}
              />
            }
          />
        </div>
      </div>

      <div>
        {!loading && !workstationReadingEntries.length && (
          <NoContent
            iconName={'noContent'}
            title={emptyStateTitle}
          >
            {' '}
          </NoContent>
        )}

        {workstationReadingEntries.map(([date, readings], i) => {
          const header = (
            <div className='flex items-center text-1624 font-semibold'>
              <span>Acquisition Date:</span>
              <span className='text-dimGray ml-1'>
                {date === UNDATED_GROUP ? 'No date reported' : shortDate(date)}
              </span>
            </div>
          );

          const headerSkeleton = (
            <div className='flex items-center text-1624 font-semibold'>
              <SkeletonLine length={140} />
              <SkeletonLine length={170} className='ml-1' />
            </div>
          );

          const isCollapsed = collapsedIndex !== i;

          return (
            <div key={date} className='mb-1'>
              <Collapse
                open={!isCollapsed}
                hideChevron
                onClick={() => setCollapsedIndex(!isCollapsed ? -1 : i)}
                resize={readings.length}
                label={
                  <CollapseHeader
                    withBorder={false}
                    collapseValue={isCollapsed ? 'closed' : 'open'}
                    title={loading ? headerSkeleton : header}
                  />
                }
              >
                <div
                  id='QCT_Workstation_Readings'
                  data-testid={`QCTWorkstationGroup-${date}`}
                  className='rounded-b-sm border-t-0'
                >
                  <Table
                    pagination={false}
                    rowKey='rowKey'
                    loading={loading}
                    virtual={readings.length > VIRTUAL_ROWS_QTY}
                    columns={columns}
                    scroll={
                      readings.length > VIRTUAL_ROWS_QTY
                        ? { y: ROW_HEIGHT * VIRTUAL_ROWS_QTY }
                        : undefined
                    }
                    dataSource={readings}
                  />
                </div>
              </Collapse>
            </div>
          );
        })}
      </div>

      {!!selectedLength && (
        <BottomConfirmation
          onCancel={() => setSelected({})}
          cancelText='Clear Selection'
          cancelProps={{ type: 'link' }}
          onOk={onSend}
          className='bottom-1 mb-1'
        >
          <span className='text-dodgerBlue'>{selectedLength}</span>{' '}
          {selectedLength > 1 ? 'values' : 'value'} selected for reading
        </BottomConfirmation>
      )}
    </div>
  );
};
